// @flow
import { createSelector } from "reselect"
import { Set } from "immutable"
import type { InputSelector } from "reselect"

import {
  getAdditionalServiceData,
  SERVICES,
  SERVICE_AVAILABILITY,
  type Service,
} from "mmb/consts/additionalServices"
import { insuranceEligiblePassengersSelector } from "mmb/services/passengers/selectors"
import { segmentsEligibleForAdditionalServicesSelector } from "./segmentsEligibleForAdditionalServicesSelector"
import { isChangeAllowedInBaggageSelector } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingBaggages/services/baggagesSelector"
import { PART_TYPES } from "booking/scenes/ReservationForm/consts/reservationConsts"
import { isInsuranceEnabledSelector } from "common/services/branding/services"
import {
  bookingDataSelector,
  lowestSeatingPriceSelector,
} from "mmb/scenes/AccountBooking/components/Booking/services/selector"
import { isKiwiBrandingSelector } from "common/services/branding/selectors"
import type {
  ServiceToShow,
  DisabledTextProps,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/AdditionalServices/services/additionalServicesTypes"
import { getAllCombinationsSelector } from "../../../BookingAddBags/services/helpers"
import type { HandHoldCombinations } from "../../../BookingAddBags/services/helpers"

const {
  MEALS,
  BAGGAGE,
  SEATING,
  INSURANCE,
  SPORTS_EQUIPMENT,
  PETS,
  MUSICAL_EQUIPMENT,
  SPECIAL_ASSISTANCE,
  TRANSFER,
  AIRPORT_PARKING,
} = SERVICES

const isServiceForFlightsOnly = (service: Service) =>
  getAdditionalServiceData(service).availability === SERVICE_AVAILABILITY.FLIGHT_ONLY

const getLowestBagPrice = (combinations: HandHoldCombinations): number => {
  const minPrice = Math.min(
    ...Object.values(combinations)
      // $FlowFixMe TODO: fix
      .filter(comb => comb.price.amount > 0)
      // $FlowFixMe TODO: fix
      .map(comb => comb.price.amount),
  )

  return minPrice === Infinity ? 0 : minPrice
}

// eslint-disable-next-line flowtype/generic-spacing
export const additionalServicesToShowSelector: InputSelector<
  any,
  *,
  $ReadOnlyArray<ServiceToShow>,
> = createSelector(
  [
    bookingDataSelector,
    isInsuranceEnabledSelector,
    isKiwiBrandingSelector,
    insuranceEligiblePassengersSelector,
    segmentsEligibleForAdditionalServicesSelector,
    isChangeAllowedInBaggageSelector,
    lowestSeatingPriceSelector,
    getAllCombinationsSelector,
  ],
  (
    booking,
    isInsuranceEnabled,
    isKiwiBranding,
    insuranceEligiblePassengers,
    segmentsEligibleForAdditionalServices,
    isChangeAllowedInBaggage,
    lowestSeatingPrice,
    bagsCombinations,
  ): ServiceToShow[] => {
    const { journey, insurancePricing, config } = booking
    const { hasHoldBagsAllowed } = journey
    const journeyTypes = journey.getTypes()
    const hasFlightPart = journeyTypes.hasIn(Set.of(PART_TYPES.FLIGHT))
    const hasSegmentsEligibleForAdditionalServices = segmentsEligibleForAdditionalServices.size > 0
    const includesGroundTransport =
      journeyTypes.includes(PART_TYPES.TRAIN) || journeyTypes.includes(PART_TYPES.BUS)

    //
    // Define Lowest prices
    //

    const bagLowestprice = getLowestBagPrice(bagsCombinations)
    // Remove "none" insurance tariff because it’s always 0
    const { none, ...insurancePrices } = insurancePricing
    const insurancePricesArr = Object.values(insurancePrices)
    // $FlowFixMe
    const insuranceLowestPrice = insurancePricesArr.length ? Math.min(...insurancePricesArr) : null

    const lowestPrices: { [Service]: ?number } = {
      [BAGGAGE]: parseFloat(bagLowestprice) > 0 ? bagLowestprice : null,
      [SEATING]: lowestSeatingPrice > 0 ? lowestSeatingPrice : null,
      [INSURANCE]: insuranceLowestPrice > 0 ? insuranceLowestPrice : null,
    }

    //
    // Determine which services to display
    //

    const services: { [Service]: boolean } = {
      [BAGGAGE]: true, // we want to display baggage even if the booking is cabin bag only
      [SEATING]: hasFlightPart,
      [INSURANCE]: isInsuranceEnabled && Boolean(insuranceEligiblePassengers.length),
      [AIRPORT_PARKING]: hasFlightPart && isKiwiBranding,
      [MEALS]: hasFlightPart,
      [TRANSFER]: hasFlightPart && isKiwiBranding,
      [SPECIAL_ASSISTANCE]: hasFlightPart,
      [SPORTS_EQUIPMENT]: hasFlightPart && hasHoldBagsAllowed,
      [PETS]: hasFlightPart && hasHoldBagsAllowed,
      [MUSICAL_EQUIPMENT]: hasFlightPart && hasHoldBagsAllowed,
    }

    //
    // Which services to have disabled text
    //

    const additionalServicesTooLateText: ?DisabledTextProps = !hasSegmentsEligibleForAdditionalServices
      ? {
          hours: config.allowedToChange.additionalServices,
          text: __("mmb.additional_services.description.late"),
        }
      : null

    const disabledTexts: { [Service]: ?DisabledTextProps } = {
      [BAGGAGE]: !isChangeAllowedInBaggage
        ? {
            hours: config.allowedToChange.bags,
            text: __("mmb.additional_services.description.late"),
          }
        : null,
      [SPECIAL_ASSISTANCE]: additionalServicesTooLateText,
      [SPORTS_EQUIPMENT]: additionalServicesTooLateText,
      [PETS]: additionalServicesTooLateText,
      [MUSICAL_EQUIPMENT]: additionalServicesTooLateText,
    }

    // -------------------------------------

    const servicesToShow: ServiceToShow[] = Object.entries(services)
      .map(([key, isAvailable]) => {
        // $FlowFixMe -> object key doesn't inherit the enum type
        const category: Service = key

        if (!isAvailable) return null

        return {
          category,
          showFlightsOnlyLabel: isServiceForFlightsOnly(category) && includesGroundTransport,
          lowestPrice: lowestPrices[category],
          disabledText: disabledTexts[category],
        }
      })
      .filter(Boolean)

    const availableServices = servicesToShow.filter(service => !service.disabledText)
    const disabledServices = servicesToShow.filter(service => service.disabledText)

    return [...availableServices, ...disabledServices]
  },
)

// eslint-disable-next-line flowtype/generic-spacing
export const isAdditionalServiceAvailableSelector: InputSelector<
  any,
  *,
  (Service) => boolean,
> = createSelector(
  additionalServicesToShowSelector,
  servicesToShow => {
    return category => {
      const service = servicesToShow.find(s => s.category === category)
      return Boolean(service && !service?.disabledText)
    }
  },
)

export default additionalServicesToShowSelector
